/**
 * Sketch-object type contract.
 *
 * A sketch-object TYPE (bath, chair, base cabinet, …) is a code-defined
 * catalog entry: anchor kind, placement constraint, default footprint, unique
 * typed params and a 2D glyph (SVG asset or parametric builder). Placed
 * INSTANCES are SKETCH_OBJECT annotations whose data lives entirely in
 * reactive properties (see SketchObjectPropertyGetters in potree-shared).
 */

import type { FunctionComponent, SVGProps } from 'react';
import type { Object3D } from 'three';
import { t } from '../translation/markers';
import type { Pt2 } from '../utils/FloorMesh3DBuilder';
import type { ParamSchema, ParamValues } from './paramSchema';

/** UI icon component (an imported `*.svg?react` or hand-written SVG FC). */
export type SketchObjectTypeIcon = FunctionComponent<SVGProps<SVGSVGElement>>;

export enum SketchObjectAnchorKind {
  /** Single anchor point + rotation (chair, bath, toilet). */
  POINT = 'POINT',
  /** Two anchor points / a run along a wall (cabinets, counters). */
  LINE = 'LINE',
  /** Free polygon footprint (kitchen island, custom shapes). */
  POLYGON = 'POLYGON',
}

export enum SketchObjectCategory {
  BATHROOM = 'BATHROOM',
  KITCHEN = 'KITCHEN',
  FURNITURE = 'FURNITURE',
  FIXTURES = 'FIXTURES',
  STRUCTURE = 'STRUCTURE',
}

/** Human labels for the library panel category headers. */
export const SKETCH_OBJECT_CATEGORY_LABELS: Record<SketchObjectCategory, string> = {
  [SketchObjectCategory.BATHROOM]: t('Bathroom'),
  [SketchObjectCategory.KITCHEN]: t('Kitchen'),
  [SketchObjectCategory.FURNITURE]: t('Furniture'),
  [SketchObjectCategory.FIXTURES]: t('Fixtures'),
  [SketchObjectCategory.STRUCTURE]: t('Structure'),
};

/** Label of the fixed dimensions category (DIMENSION_CATEGORY_ID). */
export const DIMENSION_CATEGORY_LABEL = t('Dimensions');

/**
 * Placement constraint of a type. Extensible union — a new constraint kind
 * needs a member here plus a ConstraintStrategy in constraints/.
 */
export type ConstraintSpec =
  | { kind: 'FREE' }
  | {
    kind: 'WALL';
    /** Default distance between the wall face and the back of the object (m). */
    defaultInset?: number;
  };

/**
 * Local frame of one placed object in world XY at height `z` (same role as
 * OpeningFrame for door/window glyphs).
 *  - `dir` — unit local X axis (width direction; along the wall for
 *    wall-attached objects)
 *  - `norm` — unit local Y axis (depth direction; for wall-attached objects it
 *    points AWAY from the wall into the room, so local −Y is the object back)
 */
export interface SketchObjectFrame {
  center: Pt2;
  dir: Pt2;
  norm: Pt2;
  /** Footprint size along `dir` (m). */
  width: number;
  /** Footprint size along `norm` (m). */
  depth: number;
  z: number;
}

/**
 * 2D glyph of a type. Both variants produce flat line-segment positions
 * `[x1,y1,z, x2,y2,z, ...]` (every 6 numbers = one segment), the same format
 * the door/window glyphs feed to LineSegments2.
 */
export type GlyphSpec<S extends ParamSchema> =
  | {
    kind: 'svg';
    /** Cache key for the parsed asset — unique per SVG file. */
    assetKey: string;
    /** Raw SVG source (Vite `?raw` import). */
    svgSource: string;
  }
  | {
    kind: 'parametric';
    build: (frame: SketchObjectFrame, params: ParamValues<S>) => number[];
  };

/** One extra DEPTH resize handle of a LINE-run type: frame-coord position and
 *  which norm-side edge it drags (+1 = the +norm edge, −1 = the −norm edge;
 *  the opposite edge stays pinned during the drag). */
export interface SketchObjectDepthHandleSpec {
  u: number;
  n: number;
  side: 1 | -1;
  /**
   * Custom drag semantics: called per drag move with the cursor's distance
   * from the PINNED edge (m, along norm toward `side`). The callback updates
   * the type's own params through the property service and returns the new
   * footprint depth; the tool then writes the depth property and shifts the
   * anchor so the pinned edge stays put. Omitted → the drag writes the depth
   * property directly (the whole pulled edge follows the cursor). MUST read
   * live param values per call — a drag is many moves.
   */
  apply?: (objectId: string, pulledDepth: number) => number;
}

/** Custom placement of a selected run's resize handles (see
 *  {@link SketchObjectTypeDefinition.resizeHandles}). All positions are frame
 *  coords `[u, n]`. */
export interface SketchObjectResizeHandleLayout {
  /** Handle dragging the −dir anchor endpoint (default: mid-depth on the −dir edge). */
  start?: readonly [number, number];
  /** Handle dragging the +dir anchor endpoint (default: mid-depth on the +dir edge). */
  end?: readonly [number, number];
  /** Extra depth handles (default: none). */
  depth?: ReadonlyArray<SketchObjectDepthHandleSpec>;
}

export interface SketchObjectTypeDefinition<S extends ParamSchema> {
  /**
   * Registry id, persisted in `sketchobject_type_id` and namespacing all param
   * property identifiers. SCHEMA — never rename after release.
   */
  id: string;
  label: string;
  /** A {@link SketchObjectCategory} or (custom types) an organisation category id. */
  category: string;
  anchor: SketchObjectAnchorKind;
  constraint: ConstraintSpec;
  /** Default footprint (m); seeds `sketchobject_width` / `sketchobject_depth`. */
  defaultSize: { width: number; depth: number };
  /** Unique typed params of this type (may be `{}`). */
  params: S;
  glyph: GlyphSpec<S>;
  /**
   * Optional dedicated UI icon (left-panel rows, export lists, …). When
   * omitted, UIs auto-generate an icon from the type's 2D glyph — see
   * `getSketchObjectTypeIcon` in `typeIcon.tsx` — so every type always has
   * a type-specific icon.
   */
  icon?: SketchObjectTypeIcon;
  /**
   * Extra glyph segments drawn ONLY while the object is selected (same flat
   * segment format as `glyph`) — e.g. the beam's centerline.
   */
  selectionGlyph?: (frame: SketchObjectFrame, params: ParamValues<S>) => number[];
  /**
   * LINE + FREE types only: draw as a two-point run ANYWHERE, wall-editor
   * style — click-click (or drag) between two free points, with endpoints
   * snapping onto nearby walls — instead of stamping at the default size
   * off-wall.
   */
  drawLikeWall?: boolean;
  /**
   * A dimension annotation (drawLikeWall runs only): placed by the design
   * tab's dimension tool instead of the objects library, labelled with the
   * measured length, never clipped by walls.
   */
  dimension?: boolean;
  /**
   * LINE + FREE runs only: custom placement of the selected-run resize
   * handles. Runs whose glyph is NOT centered on the run axis (multi-flight
   * stairs) use this to put the run-end handles on the stair's actual entry
   * flight and to add DEPTH handles on its other ends — dragging a depth
   * handle moves that norm-side edge with the cursor while the opposite edge
   * stays pinned (the tool adjusts the anchor and the depth property
   * together).
   */
  resizeHandles?: (frame: SketchObjectFrame, params: ParamValues<S>) => SketchObjectResizeHandleLayout;
  /**
   * Keep coupled params consistent after a property of a placed object
   * changes — e.g. the stair's step size ↔ number of steps, both following
   * the run length. Called with the changed key: a param key of this type,
   * or the shared `'width'` / `'depth'`. Implementations write back through
   * the property service and MUST no-op when values already agree — equal
   * writes don't notify, which is what terminates the mutual-update cycle.
   */
  syncParams?: (objectId: string, changedKey: string) => void;
  /**
   * 3D representation slot — part of the contract so types can grow a 3D body
   * without changing shape, but NOT called anywhere yet (2D-only v1).
   */
  build3D?: (frame: SketchObjectFrame, params: ParamValues<S>) => Object3D | null;
}
